import { useEffect } from 'react'
import { Outlet, Link, useNavigate, useLocation } from 'react-router-dom'
import { motion, AnimatePresence } from 'framer-motion'
import { ArrowLeft, ArrowRight } from 'lucide-react'
import { GradientOrb, NoiseTexture, Button } from '@personic/shared'
import { ProgressStepper } from '@/components/onboarding/ProgressStepper'
import { useOnboardingStore, ONBOARDING_STEPS } from '@/stores/onboardingStore'

export default function OnboardingLayout() {
  const navigate = useNavigate()
  const location = useLocation()
  const { currentStep, setStep, nextStep, prevStep, isStepComplete } = useOnboardingStore()

  // Sync URL with store state
  useEffect(() => {
    const pathParts = location.pathname.split('/')
    const currentPath = pathParts[pathParts.length - 1]
    const stepFromPath = ONBOARDING_STEPS.find((s) => s.path === currentPath)

    if (stepFromPath && stepFromPath.number !== currentStep) {
      setStep(stepFromPath.number)
    }
  }, [location.pathname, currentStep, setStep])

  const handleNext = () => {
    const nextStepNum = currentStep + 1
    if (nextStepNum <= ONBOARDING_STEPS.length) {
      const nextStepObj = ONBOARDING_STEPS[nextStepNum - 1]
      nextStep()
      navigate(`/onboarding/${nextStepObj.path}`)
    }
  }

  const handlePrev = () => {
    const prevStepNum = currentStep - 1
    if (prevStepNum >= 1) {
      const prevStepObj = ONBOARDING_STEPS[prevStepNum - 1]
      prevStep()
      navigate(`/onboarding/${prevStepObj.path}`)
    }
  }

  const handleSkip = () => {
    handleNext()
  }

  const currentStepObj = ONBOARDING_STEPS[currentStep - 1]
  const isFirstStep = currentStep === 1
  const isLastStep = currentStep === ONBOARDING_STEPS.length
  const canProceed = isStepComplete(currentStep)

  // Some steps are skippable (like Compliance and Team invites)
  const isSkippable = currentStep === 5 || currentStep === 6 // Compliance and Team invite steps

  // Check if we're in an attestation sub-route (shouldn't show nav buttons)
  const isAttestationSubRoute = location.pathname.includes('/attestation/') &&
    (location.pathname.includes('/frameworks') ||
     location.pathname.includes('/form/') ||
     location.pathname.includes('/confirmation'))

  return (
    <div className="min-h-screen relative flex flex-col overflow-hidden">
      {/* Background */}
      <div className="fixed inset-0 bg-gradient-to-br from-midnight-950 via-midnight-900 to-midnight-800" />

      {/* Gradient Orbs */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <GradientOrb
          color="copper"
          size="xl"
          position={{ top: '-15%', right: '-10%' }}
          opacity={25}
          blur="xl"
        />
        <GradientOrb
          color="sage"
          size="lg"
          position={{ bottom: '-10%', left: '-5%' }}
          opacity={20}
          blur="xl"
        />
        <GradientOrb
          color="midnight"
          size="md"
          position={{ top: '60%', right: '20%' }}
          opacity={15}
          blur="lg"
        />
      </div>

      {/* Noise Texture Overlay */}
      <NoiseTexture opacity={4} className="fixed inset-0 z-10" />

      {/* Header */}
      <header className="relative z-20 py-6 px-4">
        <div className="max-w-4xl mx-auto flex items-center justify-between">
          <Link to="/" className="block">
            <span className="text-2xl font-heading font-bold text-white">
              Personic
            </span>
          </Link>
        </div>
      </header>

      {/* Progress Stepper */}
      <div className="relative z-20 px-4 py-4">
        <div className="max-w-4xl mx-auto">
          <ProgressStepper currentStep={currentStep} />
        </div>
      </div>

      {/* Content */}
      <div className="relative z-20 flex-1 flex flex-col items-center px-4 py-8">
        {/* Card Container */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="w-full max-w-4xl"
        >
          <div className="relative">
            {/* Glass morphism card */}
            <div className="bg-white/95 backdrop-blur-xl rounded-3xl shadow-2xl border border-white/20 p-8 md:p-12 relative overflow-hidden min-h-[500px]">
              {/* Subtle inner glow */}
              <div className="absolute inset-0 bg-gradient-to-br from-white/50 via-transparent to-transparent pointer-events-none" />

              {/* Content */}
              <div className="relative z-10">
                <AnimatePresence mode="wait">
                  <motion.div
                    key={currentStepObj?.path}
                    initial={{ opacity: 0, x: 20 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: -20 }}
                    transition={{ duration: 0.3 }}
                  >
                    <Outlet />
                  </motion.div>
                </AnimatePresence>
              </div>
            </div>

            {/* Navigation Buttons - Hide for attestation sub-routes */}
            {!isAttestationSubRoute && (
              <div className="mt-6 flex items-center justify-between">
                {/* Back Button */}
                <div>
                  {!isFirstStep && !isLastStep && (
                    <Button
                      type="button"
                      variant="ghost"
                      onClick={handlePrev}
                      leftIcon={<ArrowLeft size={18} />}
                      className="text-white/70 hover:text-white hover:bg-white/10"
                    >
                      Back
                    </Button>
                  )}
                </div>

                {/* Skip / Continue Buttons */}
                <div className="flex items-center gap-3">
                  {isSkippable && (
                    <Button
                      type="button"
                      variant="ghost"
                      onClick={handleSkip}
                      className="text-white/70 hover:text-white hover:bg-white/10"
                    >
                      Skip for now
                    </Button>
                  )}

                  {!isLastStep && (
                    <Button
                      type="button"
                      onClick={handleNext}
                      disabled={!canProceed}
                      rightIcon={<ArrowRight size={18} />}
                    >
                      Continue
                    </Button>
                  )}
                </div>
              </div>
            )}
          </div>
        </motion.div>
      </div>

      {/* Footer */}
      <footer className="relative z-20 py-6 px-4">
        <div className="max-w-4xl mx-auto flex items-center justify-center gap-6 text-sm">
          <Link
            to="/terms"
            className="text-white/50 hover:text-white/80 transition-colors"
          >
            Terms
          </Link>
          <Link
            to="/privacy"
            className="text-white/50 hover:text-white/80 transition-colors"
          >
            Privacy
          </Link>
          <Link
            to="/contact"
            className="text-white/50 hover:text-white/80 transition-colors"
          >
            Support
          </Link>
        </div>
        <p className="text-center text-white/30 text-xs mt-4">
          &copy; {new Date().getFullYear()} Personic AI, Inc. All rights reserved.
        </p>
      </footer>
    </div>
  )
}
